// İki-tıklama onaylama butonu — modal yerine in-place confirmation.
// İlk tıklama: armed state (amber tint + countdown bar). İkinci tıklama: confirm.
// Belirli süre içinde ikinci tıklama gelmezse auto-revert.
import { useEffect, useRef, useState, type ReactNode } from 'react'

interface Props {
  /** Normal duruma ait label (ReactNode — icon + text serbest) */
  idle: ReactNode
  /** Armed durumda gösterilen label (örn 'Emin misin? Tekrar tıkla') */
  armed: ReactNode
  /** Onay sonrası çağrılan handler */
  onConfirm: () => void | Promise<void>
  /** Armed state'in auto-revert süresi (ms) — default 3500 */
  timeoutMs?: number
  /** Ek className (boyut/marjin override için) */
  className?: string
  disabled?: boolean
}

export function DoubleConfirmButton({
  idle,
  armed,
  onConfirm,
  timeoutMs = 3500,
  className = '',
  disabled,
}: Props) {
  const [isArmed, setArmed] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  function disarm() {
    setArmed(false)
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
  }

  useEffect(() => () => disarm(), [])

  async function handleClick() {
    if (isArmed) {
      disarm()
      await onConfirm()
      return
    }
    setArmed(true)
    timer.current = setTimeout(disarm, timeoutMs)
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      onBlur={disarm}
      disabled={disabled}
      className={[
        'relative inline-flex items-center gap-1.5 h-7 px-2.5 rounded text-xs font-medium border overflow-hidden transition-all duration-200 disabled:opacity-50',
        isArmed
          ? 'bg-amber-500/15 text-amber-300 border-amber-500/50 shadow-[0_0_0_2px_rgba(245,158,11,0.15)]'
          : 'bg-transparent border-transparent text-[var(--color-text-muted)] hover:bg-[var(--color-elevated)] hover:text-[var(--color-text)]',
        className,
      ].join(' ')}
    >
      {/* Label — armed/idle değişiminde fade-zoom animasyon */}
      <span
        key={isArmed ? 'armed' : 'idle'}
        className="inline-flex items-center gap-1.5 dcb-fade-in"
      >
        {isArmed ? armed : idle}
      </span>
      {/* Countdown bar — armed iken altta sağdan sola küçülür */}
      {isArmed && (
        <span
          className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-400/70 origin-left"
          style={{ animation: `dcb-shrink ${timeoutMs}ms linear forwards` }}
        />
      )}
    </button>
  )
}
